import * as React from 'react'
import CloseIcon from '../../../assets/icons_new/CloseGray.svg'
import ModalLayout from './shared/ModalLayout'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
type ModalProps = {
  isOpen: boolean
  onClose: () => void
  title: string
  text: string
  numbers?: number[]
  onConfirm?: () => void
  actionText: string
  content?: React.ReactNode[]
  cancelText?: string
  // Optional middle button for three-way questions (yes / no / cancel), e.g. the
  // co-driver copy question, where declining is not the same as backing out.
  secondaryText?: string
  onSecondary?: () => void
}

export const ConfirmModal = ({
  isOpen,
  onClose,
  text,
  numbers,
  onConfirm,
  title,
  actionText,
  content,
  cancelText,
  secondaryText,
  onSecondary,
}: ModalProps) => {
  const { t } = useLocalize()
  if (!isOpen) return null
  return (
    <ModalLayout customZIndex={9999}>
      <div
        className={`tw-w-full tw-rounded-lg tw-bg-white tw-p-4 ${
          (content?.length ?? 0) > 12 ? 'tw-max-w-[900px]' : 'tw-max-w-[500px]'
        }`}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-pb-5">
          {title && (
            <h2 className="tw-text-xl tw-font-semibold tw-text-content-amber">
              {title}
            </h2>
          )}
          <img
            src={CloseIcon}
            onClick={onClose}
            alt="Close Icon"
            className="tw-h-5 tw-w-5"
          />
        </div>
        <div className="tw-mb-5">
          <p>{text}</p>
          {content && content.length > 0 && (
            // Long lists (e.g. deleting a whole company's drivers) flow into
            // multiple columns and scroll internally, so the action buttons stay
            // on screen without scrolling the page.
            <ul
              className="tw-mt-2 tw-max-h-[50vh] tw-list-disc tw-overflow-y-auto tw-pl-5"
              style={{
                columnCount: content.length > 40 ? 3 : content.length > 12 ? 2 : 1,
                columnGap: '2rem',
              }}
            >
              {content.map((item, index) => (
                <li key={index} className="tw-break-inside-avoid">
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="tw-flex tw-justify-end tw-gap-3 tw-pt-4">
          <button
            className="tw-rounded-lg tw-bg-secondary tw-p-3 tw-text-sm tw-font-medium tw-text-primary"
            onClick={onClose}
          >
            {cancelText ?? t('common.cancel')}
          </button>
          {onSecondary && secondaryText && (
            <button
              className="tw-rounded-lg tw-bg-secondary tw-p-3 tw-text-sm tw-font-medium tw-text-primary"
              onClick={onSecondary}
            >
              {secondaryText}
            </button>
          )}
          {onConfirm && (
            <button
              className="tw-rounded-lg tw-bg-primary tw-p-3 tw-text-sm tw-font-medium tw-text-white"
              onClick={onConfirm}
            >
              {actionText || t('common.confirm')}
            </button>
          )}
        </div>
      </div>
    </ModalLayout>
  )
}
